import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";

/**
 * Centralized styles for IconBannerV2 and its sub-components
 */

// ============================================================================
// CHANNEL ICON STYLES
// ============================================================================
// --- WAT-11563: the collapsed xs grid must clip on a ROW BOUNDARY -----------
// The cap was a literal 270px while a row measured 112px unloaded (the lazyload
// placeholder) and ~66px loaded. 270 is a multiple of NEITHER, so a row was always
// sliced — 270/112 = 2.41 (the half-circles QA photographed) and 270/66 = 4.09.
//
// 🔴 TWO EARLIER ATTEMPTS FAILED HERE — read this before touching these numbers:
//   1. A hand-measured row of `66` drifted, because the two render paths disagree: the
//      WAT-11362 OV path wraps the chip in `width:100% + aspectRatio:1` (65px) while the
//      legacy path's `channelIconMediaStyles` adds `border:4px + paddingTop:1.3px` (66.3px).
//   2. Pinning `height` on `getChannelIconInnerBoxStyles` to reconcile them did NOTHING:
//      that styles the `component="a"` box, a non-replaced INLINE element whose height and
//      vertical margin CSS ignores (see the note above that function). The row pitch never
//      moved, so the "derived" 77px row and 308px cap described a box that never existed —
//      and the tests passed because they asserted the style object, not the geometry.
//
// The pin therefore lives on `getChannelIconOuterBoxStyles` below — the `component="div"`
// FLEX ITEM that actually lays out (`ChannelIcon.tsx`) and already owned the matching width.
// Pinning the flex item's height fixes the ROW PITCH at exactly the tile size for both
// paths; the legacy path's 1.3px padding may still paint outside that box, but overflow
// never grows a layout box, so it can no longer move the boundary.
//
// The pitch IS the tile size at xs — there is no row gap to add: the outer box's xs margin
// is `0px` below, and `channelIconsContainerStyles` sets no `gap`/`rowGap`. (The 12px margin
// on the INNER box is vertically inert for the same reason as #2 — do not count it again.)
export const CHANNEL_ICON_TILE_XS = 65;
export const CHANNEL_ICON_TILE_SM = 96;
export const CHANNEL_ICON_TILE_SM_GENRE = 78;
export const COLLAPSED_ROWS_XS = 4;
export const COLLAPSED_MAX_HEIGHT_XS = CHANNEL_ICON_TILE_XS * COLLAPSED_ROWS_XS;

/**
 * The smallest height difference that means "another ROW is hidden" — never compare
 * scroll/client heights exactly.
 *
 * 🔴 Pinning the tile above fixed the row pitch but left the logo image painting ~1.3px
 * outside its 65px box (the legacy `channelIconMediaStyles` border+padding path), so a
 * collapsed grid reports `scrollHeight` a pixel or two ABOVE `clientHeight` even when nothing
 * is hidden — measured at 390px on test1: single-row categories report clientHeight 65 against
 * scrollHeight 66-67, and a 2-row one 130/131. An exact `clientHeight < scrollHeight` read that
 * pixel as a hidden row and revealed "Show More" on categories that had nothing to show, on any
 * resize/scroll. The OV-logo path (`aspectRatio` wrapper) does not overflow — myfree-directv
 * measures a clean 65/65 — which is why only the legacy-logo pages showed the defect.
 *
 * Rows are whole tiles, so real hidden content is never less than one full tile. Half a
 * tile is the floor that separates a genuine row from sub-pixel paint overflow.
 */
export const HIDDEN_ROW_MIN_XS = CHANNEL_ICON_TILE_XS / 2;

/**
 * The rendered tile box per breakpoint. ONE source for the outer box that lays the tile out
 * and for the lazyload placeholder that must reserve exactly the same thing — a hand-copied
 * literal in either place is what put the reservation and the cap out of step before.
 */
export const channelIconTileSize = (genreEnabled?: boolean) => ({
  xs: CHANNEL_ICON_TILE_XS,
  sm: genreEnabled ? CHANNEL_ICON_TILE_SM_GENRE : CHANNEL_ICON_TILE_SM,
  md: CHANNEL_ICON_TILE_SM
});

export const getChannelIconOuterBoxStyles = (theme?: IChnlInteractiveThemeUI, genreEnabled?: boolean, isClickable?: boolean) => {
  const tile = channelIconTileSize(genreEnabled);
  return {
    backgroundColor: "transparent",
    borderRadius: "100%",
    position: "relative",
    width: {
      xs: `${tile?.xs}px !important`,
      sm: `${tile?.sm}px !important`,
      md: `${tile?.md}px !important`
    },
    // WAT-11563: pinned at xs ONLY — this box is the flex item, so its height IS the
    // collapsed grid's row pitch, and COLLAPSED_MAX_HEIGHT_XS multiplies exactly this.
    // `sm: auto` is REQUIRED, not decorative: in an sx responsive object `xs` is the base
    // rule with no media query, so without it this pin would cascade to tablet/desktop and
    // change layout on breakpoints that have no cap and no bug.
    height: {
      xs: `${tile?.xs}px !important`,
      sm: "auto !important"
    },
    margin: {
      xs: "0px 0 0 0",
      sm: "8px 0 0 0",
      md: "0px 0 0 0"
    },
    padding: "0px",
    boxShadow: theme?.category?.mode === "light" ? "none" : `0px 2px 20px 0px rgba(0, 0, 0, 0.05)`,
    cursor: isClickable ? "pointer" : "auto"
  };
};

// NOTE: this styles the `component="a"` box, which is a non-replaced INLINE element — it
// sets no `display`. Per CSS 2.1 §10.3.1 and css-transforms-1, `width`, `height` and
// `transform` are all IGNORED on such a box (its real size comes from the outer box via
// the image), and a background on it paints over the empty inline fragments left behind
// once the block-level <img> is pulled into an anonymous block. So do NOT put the WAT-11362
// chip here — it renders in getComputedStyle and nowhere on screen — and do NOT put layout
// geometry here either: WAT-11563 pinned a height on this box and shipped an inert fix.
// ChannelIcon wraps the image in its own block box; the geometry lives on the outer box.
// The width/margin below are inert for the same reason and are kept only as-was.
export const getChannelIconInnerBoxStyles = (isClickable?: boolean) => ({
  backgroundColor: "transparent",
  boxShadow: `0px 2px 20px 0px rgba(0, 0, 0, 0.05)`,
  borderRadius: "100%",
  position: "relative",
  width: {
    xs: `${CHANNEL_ICON_TILE_XS}px !important`,
    sm: `${CHANNEL_ICON_TILE_SM}px !important`
  },
  margin: {
    xs: "12px 0 0 0",
    sm: "8px 0 0 0",
    md: "12px 0 0 0"
  },
  padding: "0px",
  cursor: isClickable ? "pointer" : "auto",
  "&:hover": {
    backgroundColor: isClickable ? "#1FBCF87A !important" : "transparent"
  },
  "&:active": {
    backgroundColor: isClickable ? "#1FBCF87A !important" : "transparent"
  },
  "&:focus-visible": {
    outline: "2px solid #3885E1",
    outlineOffset: "-2px"
  },
  "&:inactive": {
    backgroundColor: "#1FBCF87A !important"
  }
});

export const channelIconMediaStyles = {
  paddingTop: "1.3px",
  borderRadius: "50%",
  border: "4px solid transparent",
  width: "100%",
  height: "100%",
  // WAT-11362: contain so landscape OV logos aren't cropped (no-op for square logoUrl).
  objectFit: "contain"
};

export const channelIconTooltipPopperProps = {
  disablePortal: true,
  modifiers: [
    {
      name: "preventOverflow",
      enabled: true,
      phase: "main" as const
    },
    {
      name: "flip",
      enabled: true,
      phase: "main" as const
    },
    {
      name: "offset",
      options: {
        offset: [0, -20]
      }
    }
  ],
  sx: {
    "& .MuiTypography-root": {
      fontFamily: `roboto-regular`,
      fontSize: "14px"
    }
  }
};

export const channelIconTooltipStyles = {
  "& #popper[data-popper-escaped]": {
    visibility: "hidden"
  }
};

// ============================================================================
// CATEGORY SECTION STYLES
// ============================================================================
export const categorySectionContainerStyles = {
  display: "flex",
  flexDirection: "column",
  marginTop: "42px",
  justifyContent: "flex-start",
  maxWidth: "100%",
  minWidth: "100%"
};

// Layout-only styles for the header wrapper. Typography lives on the DtvText
// itself (see getCategoryHeaderTextStyles) rather than relying on inheritance
// from this box, so it doesn't leak onto sibling elements (e.g. the tooltip
// icon span used to pick up a stray "& span" margin from here — see WAT-11467).
export const getCategoryHeaderStyles = (theme?: IChnlInteractiveThemeUI) => ({
  ...(theme?.category?.mode === "light" && { background: theme?.channelHeaderBackground }),
  width: "100%",
  padding: "4px 20px",
  alignItems: "center",
  paddingBottom: "4px"
});

export const getCategoryHeaderTextStyles = (theme?: IChnlInteractiveThemeUI) => ({
  fontSize: "16px",
  color: theme?.category?.textColor || theme?.headerTextColor || colorPalette.neutral_white.main,
  lineHeight: {
    xs: "20.8px",
    md: "17.6px"
  },
  letterSpacing: { md: "0.48px" },
  fontWeight: {
    xs: 500,
    md: 700
  }
});

export const getCategoryTooltipStyles = (theme?: IChnlInteractiveThemeUI) => ({
  backgroundColor: `${theme?.category?.mode === "light" ? colorPalette.jet.main : colorPalette.neutral_white.main} !important`,
  color: `${theme?.category?.mode === "light" ? colorPalette.neutral_white.main : colorPalette.jet.main} !important`
});

export const channelIconsContainerStyles = (showMoreKeys?: Record<string, boolean>, categoryKey?: string) => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-5px",
  overflow: {
    xs: "hidden",
    sm: "visible"
  },
  width: "100%",
  maxHeight: {
    // A fixed cap is a false ceiling: once a category's icons exceed it the
    // extra rows overlap the next category (sm, overflow:visible) or get
    // clipped (xs expanded, overflow:hidden) — satellite "Movies & Events".
    // `none` lets the container grow to fit any channel count. xs collapsed
    // stays a whole number of rows so Show more/less still works. Trade-off: xs
    // expand no longer animates (max-height cannot transition to `none`) — instant open.
    xs: showMoreKeys?.[categoryKey || ""] ? "none" : `${COLLAPSED_MAX_HEIGHT_XS}px`,
    sm: "none"
  },
  transition: "max-height 300ms ease-in-out 100ms",
  justifyContent: {
    xs: "center",
    sm: "flex-start"
  },
  paddingBottom: { sm: "12px" }
});

// ============================================================================
// SHOW MORE BUTTON STYLES
// ============================================================================
export const showMoreButtonContainerStyles = {
  display: "block",
  visibility: "hidden",
  pointerEvents: {
    xs: "auto",
    sm: "none"
  },
  textAlign: "center",
  margin: "12px 0 12px",
  position: "relative"
};

export const getShowMoreButtonStyles = (theme?: IChnlInteractiveThemeUI) => ({
  padding: "4px 10px",
  fontSize: {
    xs: "14px",
    md: "16px"
  },
  fontWeight: 500,
  lineHeight: "18.2px",
  width: "120px",
  height: "36px",
  color: theme?.category?.mode === "light" ? colorPalette.jet.main : colorPalette.neutral_white.main,
  textDecoration: "none !important",
  transition: "all ease 200ms",
  "&:focus": {
    outline: "4px solid #1FBCF8",
    color: theme?.category?.mode === "light" ? colorPalette.jet.main : colorPalette.neutral_white.main
  },
  "&:hover": {
    textDecoration: "none !important",
    backgroundColor: theme?.category?.mode === "light" ? colorPalette.jet.main : "#FFF",
    color: theme?.category?.mode === "light" ? "#FFF" : colorPalette.jet.main,
    "& img": {
      filter:
        theme?.category?.mode === "light" ?
          "brightness(0) invert(1) !important" :
          "brightness(0) saturate(100%) invert(10%) sepia(68%) saturate(1229%) hue-rotate(186deg) brightness(95%) contrast(93%) !important"
    }
  },
  "&:active": {
    backgroundColor: theme?.category?.mode === "light" ? colorPalette.neutral_white.main : "#1FBCF83D",
    color: theme?.category?.mode === "light" ? "#1FBCF83D" : colorPalette.neutral_white.main,
    "& img": {
      filter:
        theme?.category?.mode === "light" ?
          "brightness(0) invert(0)" :
          "brightness(0) saturate(100%) invert(88%) sepia(100%) saturate(26%) hue-rotate(65deg) brightness(111%) contrast(106%)"
    }
  }
});

export const showMoreArrowIconStyles = (isExpanded?: boolean) => ({
  height: "16px",
  width: "16px",
  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform 200ms ease-in-out"
});

// ============================================================================
// MY HOME TEAM CTA BUTTON STYLES
// ============================================================================
export const getMyHomeTeamCtaStyles = (theme?: IChnlInteractiveThemeUI) => ({
  mt: { xs: "10px" },
  marginBottom: {
    xs: "5.85px",
    sm: "0px"
  },
  padding: "5px 5px",
  textDecoration: "none !important",
  alignSelf: { xs: "flex-start" },
  marginLeft: "20px",
  color: theme?.category?.textColor || colorPalette.neutral_white.main,
  "&:hover": {
    textDecoration: "none !important"
  }
});

// ============================================================================
// ADD-ON CHANNELS SECTION STYLES
// ============================================================================
export const addonSectionContainerStyles = {
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  padding: {
    xs: "24px 14px",
    sm: "11px 31px",
    md: "48px 50px"
  },
  minWidth: {
    xs: "calc(100vw - 123px)",
    sm: "calc(100vw - 275px)",
    md: "calc(100vw - 487px)",
    lg: "100%"
  },
  backgroundColor: "#21528C !important",
  order: 99,
  overflow: "hidden",
  maxWidth: "100vw",
  maxHeight: "100vh"
};

export const addonHeaderContainerStyles = {
  display: "flex",
  flexDirection: "column",
  width: "100%"
};

export const addonTitleStyles = {
  fontSize: {
    xs: "16px",
    md: "24px"
  },
  color: colorPalette.neutral_white.main,
  lineHeight: {
    xs: "20.8px",
    sm: "18.2px",
    md: "28.8px"
  },
  letterSpacing: {
    xs: "initial",
    md: "-0.48px"
  },
  fontWeight: 400,
  paddingBottom: {
    xs: "8px",
    sm: "10px",
    md: "12px"
  }
};

export const addonSubtitleStyles = {
  fontSize: {
    xs: "16px",
    sm: "14px",
    md: "20px"
  },
  color: colorPalette.neutral_white.main,
  lineHeight: {
    xs: "20.8px",
    sm: "18.2px",
    md: "26px"
  },
  fontWeight: {
    xs: 400,
    sm: 300,
    md: 400
  }
};

export const addonCategorySectionStyles = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  maxWidth: "100%",
  minWidth: "100%",
  paddingTop: {
    xs: "28px",
    sm: "16px",
    md: "40px"
  }
};

export const addonCategoryTitleStyles = {
  paddingBottom: "8px",
  fontSize: {
    xs: "16px",
    md: "20px"
  },
  color: colorPalette.neutral_white.main,
  lineHeight: {
    xs: "20.8px",
    md: "24px"
  },
  letterSpacing: { md: "-0.48px" },
  fontWeight: 500
};

export const addonChannelIconsContainerStyles = {
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-5px",
  overflowY: "hidden",
  width: "100%",
  maxHeight: "5000px",
  transition: "height 300ms ease-in-out 100ms",
  marginTop: {
    xs: "3px",
    sm: "1px"
  }
};

// ============================================================================
// MAIN CONTAINER STYLES
// ============================================================================
export const mainContainerStyles = {
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  width: "100%"
};

export const zipcodeModuleContainerStyles = {
  padding: "10px 20px 0px 20px"
};
